import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useJitsu } from '@jitsu/jitsu-react';

import { getWebApp, useCallbackRef } from '@tg-web/utils';

import { OjEvent, StackEvent, useAttendEventV2 } from '../../../shared/api';
import { getCloudStorageItem } from '../../../shared/lib/getCloudStorageItem';
import {
  shouldRetryWrite,
  writeRetryDelay,
} from '../../../shared/lib/retryWrite';
import { EventsAnalytics } from '../../../shared/model/analytics';
import { CloudStorageKeys } from '../../../shared/model/cloudStorageKeys';
import { getFeedSearch } from '../../feed';
import { usePrompt } from '../../prompt';
import { RecurrentType } from '../../recurrency';
import { AttendStatus } from '../model';

/**
 * @param source which surface is attending -- the RSVP buttons on the event
 *   view page, or a join link landing on EventInstantStatusChangePage. It is
 *   carried on the recurrence event so the two can be told apart.
 */
export function useAttendEventHandler(
  onSuccess?: () => void,
  source: 'instant' | 'view' = 'view',
) {
  const { t } = useTranslation();
  const prompt = usePrompt();
  const { analytics } = useJitsu();
  const onSuccessCallbackRef = useCallbackRef(onSuccess);

  // Three tries, then the caller is told. Joining is the whole point of an
  // invite link, and the failure it used to produce was silent -- 86 users
  // followed one and ended up with no invite and nothing on screen.
  const attendEvent = useAttendEventV2({
    retry: shouldRetryWrite,
    retryDelay: writeRetryDelay,
  });

  useEffect(() => {
    if (attendEvent.isSuccess) {
      getCloudStorageItem(CloudStorageKeys.SHOW_WELCOME).then((value) => {
        if (value) {
          getWebApp().CloudStorage.setItem(
            CloudStorageKeys.FEED_SEARCH,
            JSON.stringify(getFeedSearch(attendEvent.data.event)),
          );
        }
      });
      onSuccessCallbackRef();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendEvent.isSuccess, onSuccessCallbackRef]);

  const attendEventHandler = useCallback(
    /**
     * Resolves `true` once the request is sent, and `false` when it never is --
     * the recurrence question dismissed -- so a caller holding a pending state
     * for this attempt knows to let it go. What the handler does is unchanged.
     */
    async (
      event: OjEvent | StackEvent,
      attendStatus: AttendStatus,
      /**
       * Skip the question entirely and attend with this. No callers since the
       * confirmation screen's "only this one" toggle was removed; kept for a
       * follow-up cleanup rather than changed in a hotfix.
       */
      dependentAttendOverride?: 0 | 1,
    ) => {
      let dependentAttend = dependentAttendOverride ?? 0;
      const recurrentType = event.recurrent_type;

      if (
        dependentAttendOverride === undefined &&
        recurrentType !== RecurrentType.noRecurrency
      ) {
        if (source === 'instant') {
          // A link that says "join" should join. Asking "only this one or all
          // of them?" before anything has happened turns a one-tap invite into
          // a decision, and dismissing that dialog joined nothing at all --
          // silently, which is how a join link could leave someone with no
          // invite and no idea. The whole series is the useful default. There
          // is no way to narrow it to one occurrence afterwards: the backend
          // has no narrowing path, and where the guest should get one is an
          // open design question (docs/event-page-spec.md, PR-2).
          dependentAttend = 1;
        } else {
          const promptStatus = await prompt({
            discardButton: { label: t('all:common.buttons.cancel') },
            secondaryButton: { label: t('all:common.buttons.all_events') },
            submitButton: { label: t('all:common.buttons.only_this') },
            subtitle: t('all:event.join_dialog.title'),
            title: t('all:event.join_dialog.subtitle'),
          });
          if (promptStatus === 'close' || promptStatus === 'discard') {
            // Dismissing the dialog joins nothing, and said nothing, so a join
            // that ended here was indistinguishable from one that failed.
            analytics.track(EventsAnalytics.RECURRENCE_DECLINED, { source });

            return false;
          }
          if (promptStatus === 'secondary') {
            dependentAttend = 1;
          }
        }
      }

      attendEvent.mutate({
        body: { dependent_attend: dependentAttend, status: attendStatus },
        pathParams: { id: event.id },
      });

      return true;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analytics, prompt, source, t],
  );

  return [attendEventHandler, attendEvent] as const;
}
